import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { EntityChangedEvent } from "@vms/types";
import {
  Badge,
  Card,
  DataState,
  PageHeader,
  SearchInput,
  Table,
  formatDate,
  type Column,
} from "@vms/ui-web";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";
import { useVenueSubscription } from "../../api/ws";
import { useInvalidateOn } from "../../api/useInvalidateOn";

interface VenueDriverRow {
  id: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  anonymisedAt: string | null;
  /** Most-recent session timestamp *at this venue* (not org-wide). */
  lastSessionAtThisVenue: string | null;
  /** Count of sessions this driver has run *at this venue*. */
  sessionsAtVenue: number;
}

export function VenueDrivers() {
  const { currentOrg, currentVenue } = useOrgContext();
  const [search, setSearch] = useState("");

  const params = new URLSearchParams();
  if (search) params.set("q", search);
  const endpoint = currentVenue
    ? `/api/venues/${currentVenue.id}/drivers${params.toString() ? `?${params.toString()}` : ""}`
    : null;
  const driversQuery = useQuery<VenueDriverRow[]>(endpoint);

  // Live-invalidate: a new session at this venue may add a driver or bump a
  // last-seen timestamp. Subscribe to the venue room + refetch on `session`
  // changes scoped to this venue.
  useVenueSubscription(currentVenue?.id);
  useInvalidateOn(
    "session",
    driversQuery.refetch,
    useCallback(
      (m: EntityChangedEvent) => m.scope?.venueId === currentVenue?.id,
      [currentVenue?.id],
    ),
  );

  if (!currentOrg || !currentVenue) return null;

  const rows = driversQuery.data ?? [];

  const columns: Column<VenueDriverRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (d) => (
        <Link
          to={`/org/${currentOrg.slug}/drivers/${d.id}`}
          className="font-medium text-indigo-600 hover:text-indigo-800"
        >
          {d.displayName}
        </Link>
      ),
    },
    {
      key: "company",
      header: "Company",
      render: (d) => <span className="text-sm text-gray-700">{d.companyName || "—"}</span>,
    },
    {
      key: "email",
      header: "Email",
      render: (d) => <span className="text-sm text-gray-500">{d.email || "—"}</span>,
    },
    {
      key: "sessions",
      header: "Sessions here",
      render: (d) => (
        <Badge className="bg-gray-100 text-gray-700">{d.sessionsAtVenue}</Badge>
      ),
    },
    {
      key: "lastSeen",
      header: "Last session here",
      render: (d) =>
        d.lastSessionAtThisVenue ? (
          formatDate(d.lastSessionAtThisVenue)
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Venue drivers"
        subtitle={`Drivers who have raced at ${currentVenue.name}.`}
      />

      <Card className="p-4">
        <div className="max-w-md">
          <SearchInput onSearch={setSearch} placeholder="Search name or email" />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <DataState
          loading={driversQuery.loading}
          error={driversQuery.error}
          empty={!driversQuery.loading && rows.length === 0}
          emptyMessage={
            search
              ? "No drivers match your search."
              : "No drivers have raced at this venue yet."
          }
        >
          <Table<VenueDriverRow>
            columns={columns}
            data={rows}
            keyExtractor={(d) => d.id}
          />
        </DataState>
      </Card>
    </div>
  );
}
